import { LocaleSource, SupportedLocale } from '@amzn/red-velvet-api';
import { toOfferedLocale } from './localeUtils';

const STORAGE_KEY = 'vc_language_preference';

export type CachedPreference = {
  locale: SupportedLocale;
  source: LocaleSource;
};

type StoredPreference = CachedPreference & { alias: string };

/**
 * Remembers the language a user is set to, so reopening the picker shows it instantly instead of
 * waiting on the server.
 *
 * Holds what decided that language as well as the language itself. Assuming the user chose it would
 * report everyone whose language comes from their Amazon profile as having picked it, and analytics
 * counts those two apart.
 *
 * Kept for the browsing session only, and matched to the user it was saved for, so nobody is ever
 * shown someone else's language. Storage failures are ignored: a missing cache only costs a request.
 */
export function readCachedPreference(alias: string): CachedPreference | undefined {
  if (!alias) {
    return undefined;
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }

    const cached = JSON.parse(raw) as Partial<StoredPreference>;
    if (cached.alias !== alias) {
      return undefined;
    }

    // The language is checked again on the way out, so a stale or edited entry cannot show one the
    // site does not offer. The source is passed through as stored: it is only reported, never acted
    // on, and a source this version has yet to hear of is still the truth about where the language
    // came from. Rejecting it would re-request the preference on every page load instead.
    const locale = toOfferedLocale(cached.locale);
    return locale && cached.source ? { locale, source: cached.source } : undefined;
  } catch {
    return undefined;
  }
}

export function writeCachedPreference(alias: string, preference: CachedPreference): void {
  if (!alias) {
    return;
  }

  try {
    const entry: StoredPreference = { alias, ...preference };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    return;
  }
}
